import React, { useState, useEffect, useCallback, useContext } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import {
  Users, TrendingUp, MessageSquare, AlertTriangle, Plus, Edit2, Trash2,
  ChevronDown, ChevronRight, X, Star, Globe, Archive, Send, Ticket,
  BarChart2, ClipboardList, Megaphone, RefreshCw, Clock, Key, Copy, CheckCheck,
  Loader2, Link2, Calendar, MapPin, Zap, CheckCircle, XCircle, UserCheck,
  BookOpen, Settings, Eye, Filter,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { YearContext } from '../components/Layout/Layout';
import { ptcAPI, ptcBookingAPI } from '../services/api';

// ── helpers ───────────────────────────────────────────────
const scoreColor = s => {
  if (!s || isNaN(s)) return { bg: 'bg-gray-100', text: 'text-gray-500', bar: '#94a3b8', ring: 'ring-gray-300' };
  const n = parseFloat(s);
  if (n >= 9)  return { bg: 'bg-emerald-50',  text: 'text-emerald-700', bar: '#10b981', ring: 'ring-emerald-300' };
  if (n >= 8)  return { bg: 'bg-blue-50',     text: 'text-blue-700',    bar: '#3b82f6', ring: 'ring-blue-300' };
  if (n >= 7)  return { bg: 'bg-amber-50',    text: 'text-amber-700',   bar: '#f59e0b', ring: 'ring-amber-300' };
  return         { bg: 'bg-red-50',      text: 'text-red-700',     bar: '#ef4444', ring: 'ring-red-300' };
};

const scoreLabel = s => {
  if (!s || isNaN(s)) return '—';
  const n = parseFloat(s);
  if (n >= 9) return 'Excellent';
  if (n >= 8) return 'Good';
  if (n >= 7) return 'Average';
  return 'Needs Attention';
};

const ScorePill = ({ value, small }) => {
  const c = scoreColor(value);
  return (
    <span className={`inline-flex items-center justify-center font-bold rounded-full ring-1 ${c.bg} ${c.text} ${c.ring} ${small ? 'text-xs px-2 py-0.5 min-w-[40px]' : 'text-sm px-3 py-1 min-w-[48px]'}`}>
      {value ?? '—'}
    </span>
  );
};

const STATUS_COLORS = { draft: 'bg-gray-100 text-gray-600', published: 'bg-emerald-100 text-emerald-700', closed: 'bg-slate-100 text-slate-600' };
const PRIORITY_COLORS = { low: 'bg-gray-100 text-gray-600', medium: 'bg-blue-100 text-blue-700', high: 'bg-orange-100 text-orange-700', critical: 'bg-red-100 text-red-700' };
const TICKET_STATUS_COLORS = { open: 'bg-red-100 text-red-700', in_progress: 'bg-amber-100 text-amber-700', resolved: 'bg-emerald-100 text-emerald-700', closed: 'bg-gray-100 text-gray-500' };

const DIMENSIONS = [
  { key: 'academic_progress',    label: 'Academic Progress',    weight: '30%', icon: '📚' },
  { key: 'teacher_effectiveness',label: 'Teacher Effectiveness', weight: '25%', icon: '👩‍🏫' },
  { key: 'communication',        label: 'Communication',         weight: '20%', icon: '💬' },
  { key: 'student_development',  label: 'Student Development',   weight: '15%', icon: '🌱' },
  { key: 'school_services',      label: 'School Services',       weight: '—',   icon: '🏫' },
  { key: 'recommendation',       label: 'Recommendation (NPS)',  weight: '10%', icon: '⭐' },
];

const CONCERN_OPTIONS = ['Academic Support','Communication Gap','Homework Load','Transport / Safety','Infrastructure','Co-curricular Activities','Technology Usage','Food / Canteen','Discipline','Other'];
const IMPROVEMENT_OPTIONS = ['Reading Skills','Writing Skills','Mathematics','Communication','Public Speaking','Confidence Building','Critical Thinking','Focus / Attention','Behaviour / Discipline','Social Skills','Leadership','Responsibility'];

// ── Modal wrapper ──────────────────────────────────────────
function Modal({ title, onClose, children, wide, xl }) {
  return (
    <div className={`fixed inset-0 z-50 flex ${xl ? 'items-start pt-6' : 'items-center'} justify-center bg-black/50 p-4 overflow-y-auto`}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${xl ? 'max-w-4xl my-4' : wide ? 'max-w-3xl max-h-[90vh]' : 'max-w-lg max-h-[90vh]'} flex flex-col`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h3 className="font-bold text-gray-900 text-base">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors"><X size={18} /></button>
        </div>
        <div className={`${xl ? '' : 'overflow-y-auto flex-1'} px-6 py-4`}>{children}</div>
      </div>
    </div>
  );
}

// ── Section label ──────────────────────────────────────────
function SL({ children }) {
  return <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{children}</label>;
}

function Field({ label, children }) {
  return <div className="mb-4"><SL>{label}</SL>{children}</div>;
}

const inp = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400';
const btn = (variant = 'primary') => {
  const v = { primary: 'bg-primary-600 hover:bg-primary-700 text-white', outline: 'border border-gray-200 hover:bg-gray-50 text-gray-700', danger: 'bg-red-600 hover:bg-red-700 text-white', green: 'bg-emerald-600 hover:bg-emerald-700 text-white' };
  return `inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${v[variant]}`;
};

// ── Campaign Form Builder constants ──────────────────────────
const DEFAULT_QUESTIONS = [
  { id:'q1', type:'score',       field:'academic_progress',    emoji:'📚', title:'Academic Progress',       subtitle:'How well is the school supporting your child academically?',   question:"Overall, how satisfied are you with your child's academic progress this term?",                               required:true },
  { id:'q2', type:'score',       field:'teacher_effectiveness',emoji:'👩‍🏫', title:'Teacher Effectiveness',   subtitle:"Rate your child's class teacher",                             question:'How effectively does the teacher support and engage your child?',                                             required:true },
  { id:'q3', type:'score',       field:'communication',        emoji:'💬', title:'School Communication',    subtitle:'How well does the school keep you informed?',                 question:"How satisfied are you with the school's communication with parents?",                                          required:true },
  { id:'q4', type:'score',       field:'student_development',  emoji:'🌱', title:'Student Development',     subtitle:'Beyond academics — confidence, skills, character',            question:"How well is the school developing your child's overall personality and skills?",                               required:true },
  { id:'q5', type:'score',       field:'school_services',      emoji:'🏫', title:'School Services',         subtitle:'Infrastructure, transport, activities, facilities',           question:'How satisfied are you with school services (transport, facilities, co-curricular)?',                           required:false },
  { id:'q6', type:'score',       field:'recommendation',       emoji:'⭐', title:'Recommendation',          subtitle:'Would you recommend this school to others?',                  question:'On a scale of 1–10, how likely are you to recommend this school to other parents?',                           required:true },
  { id:'q7', type:'multiselect', field:'improvement_areas',    emoji:'🎯', title:'Child Improvement Areas', subtitle:"Select all areas where you'd like to see growth",            options:['Reading Skills','Writing Skills','Mathematics','Communication','Public Speaking','Confidence Building','Critical Thinking','Focus / Attention','Behaviour / Discipline','Social Skills','Leadership','Responsibility'], required:false, color:'blue' },
  { id:'q8', type:'multiselect', field:'concern_categories',   emoji:'⚠️', title:'Areas of Concern',        subtitle:'Select any concerns you have — we take these seriously',     options:['Academic Support','Communication Gap','Homework Load','Transport / Safety','Infrastructure','Co-curricular Activities','Technology Usage','Food / Canteen','Discipline','Other'], required:false, color:'amber' },
  { id:'q9', type:'open',        field:'open_feedback',        emoji:'✍️', title:'Open Feedback',           subtitle:'Your words matter — please share freely',                    required:false },
];

const DEFAULT_LAYOUT = { template:'classic', logoUrl:'', headerTitle:'', headerSubtitle:'', footerNote:'', showProgress:true };

const TEMPLATES = [
  { id:'classic', name:'Classic White',   desc:'Clean & minimal',    gradient:'linear-gradient(135deg,#f8fafc,#e2e8f0)', textPreview:'#1e293b' },
  { id:'ocean',   name:'Ocean Blue',      desc:'Trust & authority',  gradient:'linear-gradient(135deg,#1e40af,#3b82f6)', textPreview:'#ffffff' },
  { id:'amber',   name:'Warm Amber',      desc:'Friendly & inviting',gradient:'linear-gradient(135deg,#d97706,#f59e0b)', textPreview:'#ffffff' },
  { id:'forest',  name:'Forest Green',    desc:'Growth & learning',  gradient:'linear-gradient(135deg,#065f46,#059669)', textPreview:'#ffffff' },
  { id:'purple',  name:'Purple Prestige', desc:'Premium & elegant',  gradient:'linear-gradient(135deg,#5b21b6,#7c3aed)', textPreview:'#ffffff' },
];

const Q_TYPE_LABELS = { score:'Rating 1–10', multiselect:'Multi-select', open:'Open Text', yesno:'Yes / No' };
const Q_TYPE_COLORS = { score:'bg-blue-50 text-blue-700', multiselect:'bg-purple-50 text-purple-700', open:'bg-gray-100 text-gray-600', yesno:'bg-emerald-50 text-emerald-700' };

function parseIfString(v, fallback) {
  if (!v) return fallback;
  if (typeof v === 'object') return v;
  try { return JSON.parse(v); } catch { return fallback; }
}

// ── Campaign Modal (3-step builder) ───────────────────────────
function CampaignModal({ campaign, onSave, onClose }) {
  const [step,      setStep]      = useState(0);
  const [saving,    setSaving]    = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    name:                 campaign?.name                || '',
    start_date:           campaign?.start_date?.slice(0,10)           || '',
    end_date:             campaign?.end_date?.slice(0,10)             || '',
    description:          campaign?.description         || '',
    response_target:      campaign?.response_target     || '',
    has_slot_booking:     campaign?.has_slot_booking    ? 1 : 0,
    ptc_type:             campaign?.ptc_type            || 'physical',
    booking_start_date:   campaign?.booking_start_date?.slice(0,10)  || '',
    booking_end_date:     campaign?.booking_end_date?.slice(0,10)    || '',
    feedback_start_date:  campaign?.feedback_start_date?.slice(0,10) || '',
    feedback_end_date:    campaign?.feedback_end_date?.slice(0,10)   || '',
    cutoff_hours:         campaign?.cutoff_hours        ?? 12,
  });
  const [layout,    setLayout]    = useState(() => parseIfString(campaign?.form_layout, { ...DEFAULT_LAYOUT }));
  const [questions, setQuestions] = useState(() => parseIfString(campaign?.questions, DEFAULT_QUESTIONS.map(q => ({ ...q }))));

  const setL = (k, v) => setLayout(p => ({ ...p, [k]: v }));

  const moveQ = (idx, dir) => {
    const a = [...questions];
    const to = idx + dir;
    if (to < 0 || to >= a.length) return;
    [a[idx], a[to]] = [a[to], a[idx]];
    setQuestions(a);
  };
  const updateQ = (id, patch) => setQuestions(p => p.map(q => q.id === id ? { ...q, ...patch } : q));
  const deleteQ = id => { setQuestions(p => p.filter(q => q.id !== id)); if (editingId === id) setEditingId(null); };
  const addQ = () => {
    const id = 'q_' + Math.random().toString(36).slice(2, 7);
    const nq = { id, type:'score', field:'custom_'+id.slice(2), emoji:'❓', title:'New Question', subtitle:'', question:'', required:false };
    setQuestions(p => [...p, nq]);
    setEditingId(id);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { setStep(0); return toast.error('Campaign name is required'); }
    setSaving(true);
    try {
      const payload = { ...form, questions, form_layout: layout };
      if (campaign?.id) await ptcAPI.updateCampaign(campaign.id, payload);
      else await ptcAPI.createCampaign(payload);
      toast.success(campaign?.id ? 'Campaign updated' : 'Campaign created');
      onSave();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    setSaving(false);
  };

  const STEPS = ['Basics', 'Design', 'Questions', 'Slot Booking'];

  return (
    <Modal title={campaign?.id ? 'Edit Campaign' : 'New Campaign'} onClose={onClose} xl>
      {/* Step tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-5">
        {STEPS.map((s, i) => (
          <button key={s} onClick={() => setStep(i)}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${step === i ? 'bg-white shadow-sm text-primary-700' : 'text-gray-500 hover:text-gray-700'}`}>
            {i + 1}. {s}
          </button>
        ))}
      </div>

      {/* ── Step 0: Basics ── */}
      {step === 0 && (
        <div>
          <Field label="Campaign Name">
            <input className={inp} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. PTC Term 1 – 2026-27"/>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start Date"><input type="date" className={inp} value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))}/></Field>
            <Field label="End Date"><input type="date" className={inp} value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}/></Field>
          </div>
          <Field label="Description">
            <textarea className={inp} rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Optional — shown on the analytics dashboard"/>
          </Field>
          <Field label="Response Target">
            <input type="number" className={inp} value={form.response_target} onChange={e => setForm(p => ({ ...p, response_target: e.target.value }))} placeholder="0 = no target"/>
          </Field>
        </div>
      )}

      {/* ── Step 1: Design ── */}
      {step === 1 && (
        <div>
          <SL>Form Template</SL>
          <div className="grid grid-cols-5 gap-2 mb-5">
            {TEMPLATES.map(t => (
              <button key={t.id} type="button" onClick={() => setL('template', t.id)}
                className={`rounded-xl overflow-hidden border-2 transition-all text-left ${layout.template === t.id ? 'border-primary-500 ring-2 ring-primary-200 scale-[1.04]' : 'border-gray-100 hover:border-gray-300'}`}>
                <div className="h-14 w-full flex items-center justify-center" style={{ background: t.gradient }}>
                  {layout.template === t.id && <CheckCheck size={16} color={t.textPreview} strokeWidth={3}/>}
                </div>
                <div className="px-2 py-1.5 bg-white">
                  <p className="text-xs font-bold text-gray-800 truncate">{t.name}</p>
                  <p className="text-[10px] text-gray-400 truncate leading-tight">{t.desc}</p>
                </div>
              </button>
            ))}
          </div>

          <Field label="Logo URL">
            <input className={inp} value={layout.logoUrl || ''} onChange={e => setL('logoUrl', e.target.value)} placeholder="https://your-school.com/logo.png (optional)"/>
          </Field>
          {layout.logoUrl && (
            <div className="mb-4 flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
              <img src={layout.logoUrl} alt="logo preview" className="h-12 w-12 object-contain rounded" onError={e => { e.target.style.display = 'none'; }}/>
              <p className="text-xs text-gray-400">Logo preview — appears above form header</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Header Title">
              <input className={inp} value={layout.headerTitle || ''} onChange={e => setL('headerTitle', e.target.value)} placeholder="Parent Feedback 2026"/>
            </Field>
            <Field label="Header Subtitle">
              <input className={inp} value={layout.headerSubtitle || ''} onChange={e => setL('headerSubtitle', e.target.value)} placeholder="Your voice shapes our school"/>
            </Field>
          </div>
          <Field label="Footer Note">
            <input className={inp} value={layout.footerNote || ''} onChange={e => setL('footerNote', e.target.value)} placeholder="Your feedback is confidential and helps us improve."/>
          </Field>
          <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4 border border-gray-100">
            <div>
              <p className="text-sm font-semibold text-gray-700">Show Progress Bar</p>
              <p className="text-xs text-gray-400">Displays step counter at top of form</p>
            </div>
            <button type="button" onClick={() => setL('showProgress', !layout.showProgress)}
              className={`relative w-11 h-6 rounded-full transition-colors ${layout.showProgress !== false ? 'bg-primary-600' : 'bg-gray-300'}`}>
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-200`}
                style={{ left: layout.showProgress !== false ? '22px' : '2px' }}/>
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Questions ── */}
      {step === 2 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-500">{questions.length} question{questions.length !== 1 ? 's' : ''} · use ▲▼ to reorder</p>
            <button className={btn()} onClick={addQ}><Plus size={14}/> Add Question</button>
          </div>
          <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
            {questions.map((q, idx) => (
              <div key={q.id} className="border border-gray-100 rounded-xl bg-white shadow-sm overflow-hidden">
                {/* Question row */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <span className="text-lg leading-none">{q.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{q.title || '(untitled)'}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${Q_TYPE_COLORS[q.type] || 'bg-gray-100 text-gray-500'}`}>{Q_TYPE_LABELS[q.type] || q.type}</span>
                      {q.required && <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full font-medium">Required</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button onClick={() => moveQ(idx, -1)} disabled={idx === 0} className="p-1.5 text-gray-300 hover:text-gray-600 disabled:opacity-20 transition-colors" title="Move up">
                      <ChevronDown size={14} style={{ transform:'rotate(180deg)' }}/>
                    </button>
                    <button onClick={() => moveQ(idx, 1)} disabled={idx === questions.length - 1} className="p-1.5 text-gray-300 hover:text-gray-600 disabled:opacity-20 transition-colors" title="Move down">
                      <ChevronDown size={14}/>
                    </button>
                    <button onClick={() => setEditingId(editingId === q.id ? null : q.id)}
                      className={`p-1.5 rounded-lg transition-colors ml-1 ${editingId === q.id ? 'bg-primary-100 text-primary-700' : 'text-gray-400 hover:text-primary-600'}`}
                      title="Edit">
                      <Edit2 size={13}/>
                    </button>
                    <button onClick={() => deleteQ(q.id)} className="p-1.5 text-gray-300 hover:text-red-500 transition-colors ml-0.5" title="Delete">
                      <Trash2 size={13}/>
                    </button>
                  </div>
                </div>

                {/* Inline editor */}
                {editingId === q.id && (
                  <div className="border-t border-gray-100 bg-gray-50 px-4 py-4">
                    <div className="grid grid-cols-12 gap-3 mb-3">
                      <div className="col-span-1">
                        <SL>Icon</SL>
                        <input className={inp} value={q.emoji} onChange={e => updateQ(q.id, { emoji: e.target.value })} maxLength={4}/>
                      </div>
                      <div className="col-span-5">
                        <SL>Title</SL>
                        <input className={inp} value={q.title} onChange={e => updateQ(q.id, { title: e.target.value })} placeholder="Question section title"/>
                      </div>
                      <div className="col-span-3">
                        <SL>Type</SL>
                        <select className={inp} value={q.type} onChange={e => updateQ(q.id, { type: e.target.value })}>
                          <option value="score">Rating 1–10</option>
                          <option value="multiselect">Multi-select</option>
                          <option value="open">Open Text</option>
                          <option value="yesno">Yes / No</option>
                        </select>
                      </div>
                      <div className="col-span-3 flex flex-col justify-end pb-0.5">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <div className="relative flex-shrink-0">
                            <input type="checkbox" className="sr-only" checked={!!q.required} onChange={e => updateQ(q.id, { required: e.target.checked })}/>
                            <div className={`w-9 h-5 rounded-full transition-colors ${q.required ? 'bg-primary-600' : 'bg-gray-200'}`}/>
                            <div className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all duration-200" style={{ left: q.required ? '18px' : '2px' }}/>
                          </div>
                          <span className="text-sm text-gray-600 font-medium">Required</span>
                        </label>
                      </div>
                    </div>
                    <div className="mb-3">
                      <SL>Subtitle</SL>
                      <input className={inp} value={q.subtitle || ''} onChange={e => updateQ(q.id, { subtitle: e.target.value })} placeholder="Shown below the title on the parent's screen"/>
                    </div>
                    {(q.type === 'score' || q.type === 'yesno') && (
                      <div className="mb-3">
                        <SL>Question Text</SL>
                        <input className={inp} value={q.question || ''} onChange={e => updateQ(q.id, { question: e.target.value })} placeholder="e.g. How satisfied are you with…"/>
                      </div>
                    )}
                    {q.type === 'multiselect' && (
                      <div className="mb-3">
                        <SL>Options <span className="font-normal text-gray-400 normal-case">(one per line)</span></SL>
                        <textarea className={inp} rows={4}
                          value={(q.options || []).join('\n')}
                          onChange={e => updateQ(q.id, { options: e.target.value.split('\n').map(o => o.trim()).filter(Boolean) })}
                          placeholder={`Reading Skills\nWriting Skills\nMathematics`}/>
                        {(q.options || []).length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {(q.options || []).map((o, i) => (
                              <span key={i} className="text-xs bg-white border border-gray-200 rounded-full px-2.5 py-0.5 text-gray-600">{o}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex justify-end">
                      <button className={btn('outline')} onClick={() => setEditingId(null)}>Done</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {questions.length === 0 && (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                <p className="text-gray-400">No questions yet. Add the first one!</p>
                <button className={`${btn()} mt-3`} onClick={addQ}><Plus size={14}/> Add Question</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Step 3: Slot Booking ── */}
      {step === 3 && (
        <div className="space-y-4">
          {/* Enable toggle */}
          <div className="flex items-center justify-between bg-blue-50 rounded-2xl px-4 py-3 border border-blue-100">
            <div>
              <p className="font-semibold text-blue-900 text-sm">Enable Slot Booking</p>
              <p className="text-xs text-blue-600 mt-0.5">Allow parents to book a meeting time slot for this campaign</p>
            </div>
            <button
              onClick={() => setForm(p => ({ ...p, has_slot_booking: p.has_slot_booking ? 0 : 1 }))}
              className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${form.has_slot_booking ? 'bg-blue-600' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.has_slot_booking ? 'translate-x-6' : 'translate-x-0.5'}`}/>
            </button>
          </div>

          {form.has_slot_booking ? (
            <>
              {/* PTC Type */}
              <Field label="Meeting Type">
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v:'physical', label:'🏫 Physical', desc:'In-person at school' },
                    { v:'online',   label:'💻 Online',   desc:'Video call / link' },
                    { v:'hybrid',   label:'🔀 Hybrid',   desc:'Both options' },
                  ].map(opt => (
                    <button key={opt.v} onClick={() => setForm(p => ({ ...p, ptc_type: opt.v }))}
                      className={`rounded-xl border px-3 py-2.5 text-left transition-all ${form.ptc_type === opt.v ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
                      <p className="font-semibold text-sm">{opt.label}</p>
                      <p className="text-xs opacity-70 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </Field>

              {/* Booking window */}
              <Field label="Booking Window — when parents can book slots">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Opens</label>
                    <input type="date" className={inp} value={form.booking_start_date}
                      onChange={e => setForm(p => ({ ...p, booking_start_date: e.target.value }))}/>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Closes</label>
                    <input type="date" className={inp} value={form.booking_end_date}
                      onChange={e => setForm(p => ({ ...p, booking_end_date: e.target.value }))}/>
                  </div>
                </div>
              </Field>

              {/* Feedback window */}
              <Field label="Feedback Window — when parents can submit feedback">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Opens</label>
                    <input type="date" className={inp} value={form.feedback_start_date}
                      onChange={e => setForm(p => ({ ...p, feedback_start_date: e.target.value }))}/>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Closes</label>
                    <input type="date" className={inp} value={form.feedback_end_date}
                      onChange={e => setForm(p => ({ ...p, feedback_end_date: e.target.value }))}/>
                  </div>
                </div>
              </Field>

              {/* Cutoff hours */}
              <Field label="Cancellation Cutoff">
                <div className="flex items-center gap-3">
                  <input type="number" min={0} max={72} className={`${inp} w-24`}
                    value={form.cutoff_hours}
                    onChange={e => setForm(p => ({ ...p, cutoff_hours: parseInt(e.target.value) || 0 }))}/>
                  <span className="text-sm text-gray-500">hours before the meeting (parents cannot cancel after this)</span>
                </div>
              </Field>

              {/* Feedback form summary */}
              <div className="bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-3">
                <p className="text-sm font-semibold text-emerald-800 mb-1">📋 Linked Feedback Form</p>
                <p className="text-xs text-emerald-700">
                  After each meeting is marked <strong>Attended</strong>, parents will automatically receive a feedback link
                  using the <strong>{questions.length} question{questions.length !== 1 ? 's' : ''}</strong> configured in Step 3.
                </p>
                <button type="button" onClick={() => setStep(2)}
                  className="mt-2 text-xs text-emerald-700 underline hover:text-emerald-900">
                  ← Go to Questions to edit the feedback form
                </button>
              </div>
            </>
          ) : (
            <div className="py-10 text-center text-gray-400">
              <Calendar size={36} className="mx-auto mb-3 opacity-30"/>
              <p className="text-sm">Enable slot booking above to configure meeting settings.</p>
              <p className="text-xs mt-1 opacity-70">The feedback form (Step 3) will be sent to parents after their meeting.</p>
            </div>
          )}
        </div>
      )}

      {/* Footer nav */}
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
        <button className={btn('outline')} onClick={() => step > 0 ? setStep(s => s - 1) : onClose()}>
          {step > 0 ? '← Back' : 'Cancel'}
        </button>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 mr-2">
            {STEPS.map((_, i) => <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === step ? 'bg-primary-600' : 'bg-gray-200'}`}/>)}
          </div>
          {step < STEPS.length - 1
            ? <button className={btn()} onClick={() => setStep(s => s + 1)}>Next →</button>
            : <button className={btn()} onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : campaign?.id ? 'Save Changes' : 'Create Campaign'}</button>
          }
        </div>
      </div>
    </Modal>
  );
}

// ── Ticket Modal ───────────────────────────────────────────
function TicketModal({ ticket, campaignId, onSave, onClose }) {
  const [form, setForm] = useState({
    title:       ticket?.title       || '',
    description: ticket?.description || '',
    category:    ticket?.category    || '',
    class_name:  ticket?.class_name  || '',
    student_name:ticket?.student_name|| '',
    assigned_to: ticket?.assigned_to || '',
    priority:    ticket?.priority    || 'medium',
    status:      ticket?.status      || 'open',
    due_date:    ticket?.due_date?.slice(0,10) || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    setSaving(true);
    try {
      if (ticket?.id) await ptcAPI.updateTicket(campaignId, ticket.id, form);
      else await ptcAPI.createTicket(campaignId, form);
      toast.success('Ticket saved');
      onSave();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    setSaving(false);
  };

  return (
    <Modal title={ticket?.id ? 'Edit Action Ticket' : 'New Action Ticket'} onClose={onClose} wide>
      <Field label="Title">
        <input className={inp} value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Brief description of the issue…" />
      </Field>
      <Field label="Description">
        <textarea className={inp} rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Details, context, action required…" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Category">
          <select className={inp} value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
            <option value="">Select category</option>
            {CONCERN_OPTIONS.map(o => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Class">
          <input className={inp} value={form.class_name} onChange={e => setForm(p => ({ ...p, class_name: e.target.value }))} placeholder="e.g. Grade 5A" />
        </Field>
        <Field label="Student Name (optional)">
          <input className={inp} value={form.student_name} onChange={e => setForm(p => ({ ...p, student_name: e.target.value }))} />
        </Field>
        <Field label="Assigned To">
          <input className={inp} value={form.assigned_to} onChange={e => setForm(p => ({ ...p, assigned_to: e.target.value }))} placeholder="Teacher / Coordinator name" />
        </Field>
        <Field label="Priority">
          <select className={inp} value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </Field>
        <Field label="Status">
          <select className={inp} value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </Field>
        <Field label="Due Date">
          <input type="date" className={inp} value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} />
        </Field>
      </div>
      <div className="flex gap-3 justify-end mt-2">
        <button className={btn('outline')} onClick={onClose}>Cancel</button>
        <button className={btn()} onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Save Ticket'}</button>
      </div>
    </Modal>
  );
}

// ── Class Detail Side Panel ────────────────────────────────
function ClassDetailPanel({ campaignId, className, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ptcAPI.getClassDetail(campaignId, className)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load class detail'))
      .finally(() => setLoading(false));
  }, [campaignId, className]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="font-bold text-lg text-gray-900">{className}</h3>
            <p className="text-sm text-gray-500">{loading ? '…' : `${data?.summary?.responses || 0} responses`}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-48 text-gray-400">Loading…</div>
        ) : !data?.summary ? (
          <div className="flex items-center justify-center h-48 text-gray-400">No data</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Score Row */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Score Summary</p>
              <div className="grid grid-cols-3 gap-2">
                {DIMENSIONS.map(d => (
                  <div key={d.key} className={`rounded-xl p-3 text-center ${scoreColor(data.summary[d.key]).bg}`}>
                    <p className="text-xs text-gray-500 truncate">{d.label.split(' ')[0]}</p>
                    <p className={`text-lg font-bold mt-1 ${scoreColor(data.summary[d.key]).text}`}>{data.summary[d.key] ?? '—'}</p>
                  </div>
                ))}
                <div className="col-span-3 rounded-xl p-3 text-center bg-slate-800 text-white">
                  <p className="text-xs text-slate-300">Overall Score</p>
                  <p className="text-2xl font-bold mt-0.5">{data.summary.overall ?? '—'}</p>
                </div>
              </div>
            </div>
            {/* Improvement Areas */}
            {data.improvement_areas?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Child Improvement Areas</p>
                <div className="flex flex-wrap gap-2">
                  {data.improvement_areas.map(a => (
                    <span key={a.name} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-medium px-2 py-1 rounded-full">
                      {a.name} <span className="bg-blue-200 text-blue-800 px-1 rounded-full font-bold">{a.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* Concern Categories */}
            {data.concern_categories?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Concern Areas</p>
                <div className="flex flex-wrap gap-2">
                  {data.concern_categories.map(a => (
                    <span key={a.name} className="inline-flex items-center gap-1 bg-red-50 text-red-700 text-xs font-medium px-2 py-1 rounded-full">
                      {a.name} <span className="bg-red-200 text-red-800 px-1 rounded-full font-bold">{a.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* Appreciate */}
            {data.appreciate?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">What Parents Appreciate</p>
                <div className="space-y-2">
                  {data.appreciate.slice(0,5).map((t, i) => (
                    <p key={i} className="text-sm text-gray-700 bg-emerald-50 rounded-lg px-3 py-2 border-l-2 border-emerald-400">"{t}"</p>
                  ))}
                </div>
              </div>
            )}
            {/* Improve */}
            {data.improve?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Improvement Suggestions</p>
                <div className="space-y-2">
                  {data.improve.slice(0,5).map((t, i) => (
                    <p key={i} className="text-sm text-gray-700 bg-amber-50 rounded-lg px-3 py-2 border-l-2 border-amber-400">"{t}"</p>
                  ))}
                </div>
              </div>
            )}
            {/* Management Concerns */}
            {data.management_concerns?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-red-400 uppercase tracking-wide mb-2 flex items-center gap-1"><AlertTriangle size={12} /> Concerns for Management</p>
                <div className="space-y-2">
                  {data.management_concerns.map((c, i) => (
                    <div key={i} className="bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                      <p className="text-xs font-semibold text-red-600">{c.student}</p>
                      <p className="text-sm text-gray-700 mt-0.5">"{c.text}"</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Token Management Modal ─────────────────────────────────
function TokensModal({ campaign, onClose }) {
  const [tokens,    setTokens]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [generating,setGenerating]= useState(false);
  const [copiedId,  setCopiedId]  = useState(null);
  const [filter,    setFilter]    = useState('');

  const portalBase = `${window.location.origin}/feedback/`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ptcAPI.getTokens(campaign.id);
      setTokens(r.data || []);
    } catch { toast.error('Failed to load tokens'); }
    setLoading(false);
  }, [campaign.id]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    if (!window.confirm(`Generate feedback links for all students in this campaign? Existing links will not be changed.`)) return;
    setGenerating(true);
    try {
      const r = await ptcAPI.generateTokens(campaign.id);
      toast.success(`${r.data.generated} new links generated (${r.data.skipped} already existed)`);
      await load();
    } catch (e) { toast.error(e.response?.data?.error || 'Generation failed'); }
    setGenerating(false);
  };

  const handleDelete = async (tok) => {
    if (!window.confirm(`Delete link for ${tok.student_name}? Parent will lose access.`)) return;
    try {
      await ptcAPI.deleteToken(campaign.id, tok.id);
      toast.success('Link deleted');
      setTokens(prev => prev.filter(t => t.id !== tok.id));
    } catch (e) { toast.error(e.response?.data?.error || 'Delete failed'); }
  };

  const copyLink = (tok) => {
    const url = `${portalBase}${tok.token}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(tok.id);
      toast.success('Link copied');
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const copyAll = () => {
    const lines = filtered.map(t => `${t.student_name} (${t.class_name}): ${portalBase}${t.token}`).join('\n');
    navigator.clipboard.writeText(lines).then(() => toast.success(`${filtered.length} links copied`));
  };

  const filtered = tokens.filter(t => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (t.student_name||'').toLowerCase().includes(q)
        || (t.class_name||'').toLowerCase().includes(q)
        || (t.parent_name||'').toLowerCase().includes(q);
  });

  const total    = tokens.length;
  const used     = tokens.filter(t => t.is_used).length;
  const pending  = total - used;

  return (
    <Modal title={`Feedback Links — ${campaign.name}`} onClose={onClose} wide>
      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { label: 'Total Links', val: total,   color: 'bg-blue-50 text-blue-700' },
          { label: 'Submitted',   val: used,     color: 'bg-emerald-50 text-emerald-700' },
          { label: 'Pending',     val: pending,  color: 'bg-amber-50 text-amber-700' },
        ].map(s => (
          <div key={s.label} className={`rounded-xl p-3 text-center ${s.color}`}>
            <p className="text-xl font-black">{s.val}</p>
            <p className="text-xs font-medium mt-0.5 opacity-75">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <input
          className="flex-1 min-w-[160px] border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          placeholder="Filter by student, class, parent…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
        {filtered.length > 0 && (
          <button className={btn('outline')} onClick={copyAll}>
            <Copy size={13} /> Copy All ({filtered.length})
          </button>
        )}
        <button className={btn()} onClick={handleGenerate} disabled={generating}>
          {generating ? <><Loader2 size={13} className="animate-spin" /> Generating…</> : <><Key size={13} /> Generate Links</>}
        </button>
      </div>

      {/* Token list */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400 gap-2">
          <Loader2 size={18} className="animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <Link2 size={32} className="mx-auto mb-2 opacity-40" />
          <p className="font-medium text-sm">{tokens.length === 0 ? 'No links yet' : 'No matches'}</p>
          {tokens.length === 0 && campaign.status !== 'closed' && (
            <p className="text-xs mt-1">Click "Generate Links" to create feedback URLs for all enrolled students</p>
          )}
        </div>
      ) : (
        <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
          {filtered.map(tok => (
            <div key={tok.id} className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${tok.is_used ? 'border-emerald-100 bg-emerald-50/40' : 'border-gray-100 bg-white'}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-gray-900 truncate">{tok.student_name}</span>
                  <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{tok.class_name}</span>
                  {tok.is_used && (
                    <span className="text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1">
                      <CheckCheck size={10} /> Submitted
                    </span>
                  )}
                </div>
                {tok.parent_name && <p className="text-xs text-gray-400 mt-0.5">Parent: {tok.parent_name}{tok.parent_mobile ? ` · ${tok.parent_mobile}` : ''}</p>}
                <p className="text-xs text-gray-300 font-mono mt-0.5 truncate">{portalBase}{tok.token}</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => copyLink(tok)}
                  title="Copy link"
                  className="p-1.5 rounded-lg hover:bg-primary-50 text-gray-400 hover:text-primary-600 transition-colors"
                >
                  {copiedId === tok.id ? <CheckCheck size={15} className="text-emerald-500" /> : <Copy size={15} />}
                </button>
                {!tok.is_used && (
                  <button
                    onClick={() => handleDelete(tok)}
                    title="Delete link"
                    className="p-1.5 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ── Tab: Campaigns ─────────────────────────────────────────
function CampaignsTab({ campaigns, onRefresh, yearId }) {
  const [modal,       setModal]       = useState(null); // null | 'create' | campaign object
  const [tokensFor,   setTokensFor]   = useState(null); // campaign object | null

  const handlePublish = async (c) => {
    if (!window.confirm(`Publish "${c.name}"? Parents will be able to submit feedback.`)) return;
    try { await ptcAPI.publish(c.id); toast.success('Campaign published'); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };
  const handleClose = async (c) => {
    if (!window.confirm(`Close "${c.name}"? No more submissions will be accepted.`)) return;
    try { await ptcAPI.close(c.id); toast.success('Campaign closed'); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };
  const handleDelete = async (c) => {
    if (!window.confirm(`Delete "${c.name}" and ALL responses? This cannot be undone.`)) return;
    try { await ptcAPI.deleteCampaign(c.id); toast.success('Deleted'); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-gray-500">{campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''} this year</p>
        <button className={btn()} onClick={() => setModal('create')}><Plus size={15} /> New Campaign</button>
      </div>
      {campaigns.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Megaphone size={40} className="mx-auto mb-3 opacity-40" />
          <p className="font-medium">No campaigns yet</p>
          <p className="text-sm mt-1">Create one to start collecting parent feedback</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => {
            const classes = Array.isArray(c.classes_included) ? c.classes_included : (() => { try { return JSON.parse(c.classes_included || '[]'); } catch { return []; } })();
            const pct = c.response_target > 0 ? Math.min(100, Math.round((c.submitted_count || 0) / c.response_target * 100)) : null;
            return (
              <div key={c.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-gray-900 text-base">{c.name}</h3>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[c.status]}`}>{c.status}</span>
                    </div>
                    {c.description && <p className="text-sm text-gray-500 mt-1 line-clamp-1">{c.description}</p>}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400 flex-wrap">
                      {c.start_date && <span>📅 {c.start_date?.slice(0,10)} → {c.end_date?.slice(0,10) || '…'}</span>}
                      <span><Users size={11} className="inline mr-1" />{c.submitted_count || 0} submitted</span>
                      {c.avg_score && <ScorePill value={c.avg_score} small />}
                      {classes.length > 0 && <span>{classes.length} class{classes.length !== 1 ? 'es' : ''}</span>}
                    </div>
                    {pct !== null && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Response rate</span><span>{c.submitted_count}/{c.response_target} ({pct}%)</span></div>
                        <div className="w-full bg-gray-100 rounded-full h-1.5"><div className="h-1.5 rounded-full bg-primary-500 transition-all" style={{ width: `${pct}%` }} /></div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {c.status === 'draft'     && <button className={btn('green')}   onClick={() => handlePublish(c)}><Send size={13} /> Publish</button>}
                    {c.status === 'published' && <button className={btn('outline')} onClick={() => handleClose(c)}><Archive size={13} /> Close</button>}
                    <button className={btn('outline')} onClick={() => setTokensFor(c)} title="Manage feedback links"><Key size={13} /> Links</button>
                    <button className={btn('outline')} onClick={() => setModal(c)}><Edit2 size={13} /></button>
                    <button className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold border border-red-200 text-red-600 hover:bg-red-50 transition-colors" onClick={() => handleDelete(c)}><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {modal && (
        <CampaignModal campaign={modal === 'create' ? null : modal} onClose={() => setModal(null)} onSave={() => { setModal(null); onRefresh(); }} />
      )}
      {tokensFor && (
        <TokensModal campaign={tokensFor} onClose={() => setTokensFor(null)} />
      )}
    </div>
  );
}

// ── Tab: Dashboard ─────────────────────────────────────────
function DashboardTab({ campaignId }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [selClass, setSelClass] = useState(null);

  const load = useCallback(() => {
    if (!campaignId) return;
    setLoading(true);
    ptcAPI.getSummary(campaignId)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [campaignId]);

  useEffect(load, [load]);

  if (!campaignId) return <div className="py-16 text-center text-gray-400">Select a campaign to view the dashboard</div>;
  if (loading)     return <div className="py-16 text-center text-gray-400">Loading dashboard…</div>;
  if (!data)       return null;

  const { kpis, classRankings, topConcerns, topImprovements, alerts } = data;

  const kpiCards = [
    { label: 'Total Responses', value: kpis.total_responses, icon: Users,      color: 'bg-blue-50 text-blue-700',    iconColor: 'text-blue-400' },
    { label: 'Avg Overall Score', value: kpis.avg_score ?? '—', icon: Star,   color: 'bg-emerald-50 text-emerald-700', iconColor: 'text-emerald-400' },
    { label: 'NPS Score',       value: kpis.nps ?? '—',    icon: TrendingUp, color: `${kpis.nps >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`, iconColor: kpis.nps >= 0 ? 'text-emerald-400' : 'text-red-400' },
    { label: 'Classes Responded', value: kpis.classes_responded, icon: Globe, color: 'bg-purple-50 text-purple-700', iconColor: 'text-purple-400' },
  ];

  const dimBars = DIMENSIONS.slice(0, 5).map(d => ({
    name: d.label.split(' ')[0],
    score: parseFloat(kpis[`avg_${d.key.replace('academic_progress','academic').replace('teacher_effectiveness','teacher').replace('student_development','development').replace('school_services','services')}`] || 0),
  }));

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {alerts?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm font-bold text-red-700 flex items-center gap-2 mb-2"><AlertTriangle size={15} /> {alerts.length} Alert{alerts.length > 1 ? 's' : ''} Requiring Attention</p>
          <div className="flex flex-wrap gap-2">
            {alerts.map((a, i) => (
              <span key={i} className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-lg font-medium">{a.class} — {a.dim}: {a.score}</span>
            ))}
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map(k => (
          <div key={k.label} className={`rounded-xl p-4 ${k.color} border border-white/50`}>
            <k.icon size={18} className={`${k.iconColor} mb-2`} />
            <p className="text-2xl font-black">{k.value}</p>
            <p className="text-xs font-medium mt-0.5 opacity-75">{k.label}</p>
          </div>
        ))}
      </div>

      {/* Dimension Scores */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <h4 className="font-bold text-gray-800 mb-4">School-wide Score Breakdown</h4>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-4">
          {DIMENSIONS.slice(0,5).map(d => {
            const avgKey = `avg_${d.key.replace('academic_progress','academic').replace('teacher_effectiveness','teacher').replace('student_development','development').replace('school_services','services')}`;
            const val = kpis[avgKey];
            const c = scoreColor(val);
            return (
              <div key={d.key} className={`rounded-xl p-3 text-center ${c.bg} border border-white`}>
                <p className="text-lg mb-1">{d.icon}</p>
                <p className={`text-xl font-black ${c.text}`}>{val ?? '—'}</p>
                <p className="text-xs text-gray-500 mt-0.5">{d.label.split(' ')[0]}</p>
                <p className="text-xs text-gray-400">{d.weight}</p>
              </div>
            );
          })}
        </div>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dimBars} barSize={36}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} tickCount={6} />
              <Tooltip formatter={v => [`${v}/10`]} />
              <Bar dataKey="score" radius={[4,4,0,0]}>
                {dimBars.map((d, i) => <Cell key={i} fill={scoreColor(d.score).bar} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Class Rankings */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <h4 className="font-bold text-gray-800 mb-4">Class Performance Rankings</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left">
                <th className="pb-2 text-xs font-semibold text-gray-400 uppercase w-8">#</th>
                <th className="pb-2 text-xs font-semibold text-gray-400 uppercase">Class</th>
                <th className="pb-2 text-xs font-semibold text-gray-400 uppercase text-center">Resp.</th>
                <th className="pb-2 text-xs font-semibold text-gray-400 uppercase text-center">Academic</th>
                <th className="pb-2 text-xs font-semibold text-gray-400 uppercase text-center">Teacher</th>
                <th className="pb-2 text-xs font-semibold text-gray-400 uppercase text-center">Comm.</th>
                <th className="pb-2 text-xs font-semibold text-gray-400 uppercase text-center">Dev.</th>
                <th className="pb-2 text-xs font-semibold text-gray-400 uppercase text-center">Rec.</th>
                <th className="pb-2 text-xs font-semibold text-gray-400 uppercase text-center">Overall</th>
                <th className="pb-2 w-6"></th>
              </tr>
            </thead>
            <tbody>
              {classRankings.map((c, i) => (
                <tr key={c.class_name} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="py-2.5 pr-2 text-gray-400 font-mono text-xs">{i + 1}</td>
                  <td className="py-2.5 font-semibold text-gray-900">{c.class_name}</td>
                  <td className="py-2.5 text-center text-gray-500 text-xs">{c.responses}</td>
                  <td className="py-2.5 text-center"><ScorePill value={c.academic_progress} small /></td>
                  <td className="py-2.5 text-center"><ScorePill value={c.teacher_effectiveness} small /></td>
                  <td className="py-2.5 text-center"><ScorePill value={c.communication} small /></td>
                  <td className="py-2.5 text-center"><ScorePill value={c.student_development} small /></td>
                  <td className="py-2.5 text-center"><ScorePill value={c.recommendation} small /></td>
                  <td className="py-2.5 text-center"><ScorePill value={c.overall_score} /></td>
                  <td className="py-2.5">
                    <button onClick={() => setSelClass(c.class_name)} className="text-gray-300 hover:text-primary-500 transition-colors"><ChevronRight size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Concerns + Improvements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {topConcerns?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h4 className="font-bold text-gray-800 mb-4">Top Concern Areas</h4>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topConcerns.slice(0,8)} layout="vertical" barSize={14}>
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#ef4444" radius={[0,3,3,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {topImprovements?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h4 className="font-bold text-gray-800 mb-4">Child Improvement Areas</h4>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topImprovements.slice(0,8)} layout="vertical" barSize={14}>
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3b82f6" radius={[0,3,3,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {selClass && <ClassDetailPanel campaignId={campaignId} className={selClass} onClose={() => setSelClass(null)} />}
    </div>
  );
}

// ── Tab: Responses ─────────────────────────────────────────
function ResponsesTab({ campaignId }) {
  const [responses, setResponses] = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [filter,    setFilter]    = useState({ class_name: '', status: '' });
  const [expanded,  setExpanded]  = useState(null);

  const load = useCallback(() => {
    if (!campaignId) return;
    setLoading(true);
    ptcAPI.getResponses(campaignId, filter)
      .then(r => setResponses(r.data))
      .catch(() => toast.error('Failed to load responses'))
      .finally(() => setLoading(false));
  }, [campaignId, filter]);

  useEffect(load, [load]);

  const classes = [...new Set(responses.map(r => r.class_name).filter(Boolean))].sort();

  const handleDelete = async (rid) => {
    if (!window.confirm('Delete this response?')) return;
    try { await ptcAPI.deleteResponse(campaignId, rid); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  if (!campaignId) return <div className="py-16 text-center text-gray-400">Select a campaign</div>;

  return (
    <div>
      <div className="flex gap-3 mb-5 flex-wrap items-center">
        <select className={`${inp} w-40`} value={filter.class_name} onChange={e => setFilter(p => ({ ...p, class_name: e.target.value }))}>
          <option value="">All Classes</option>
          {classes.map(c => <option key={c}>{c}</option>)}
        </select>
        <select className={`${inp} w-36`} value={filter.status} onChange={e => setFilter(p => ({ ...p, status: e.target.value }))}>
          <option value="">All Statuses</option>
          <option value="submitted">Submitted</option>
          <option value="draft">Draft</option>
        </select>
        <button className={btn('outline')} onClick={load}><RefreshCw size={13} /></button>
        <span className="text-sm text-gray-400 ml-auto">{responses.length} response{responses.length !== 1 ? 's' : ''}</span>
      </div>
      {loading ? <p className="text-center text-gray-400 py-10">Loading…</p> : (
        <div className="space-y-2">
          {responses.map(r => (
            <div key={r.id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">{r.student_name || r.admission_no || 'Anonymous'}</span>
                    <span className="text-xs text-gray-400">{r.class_name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[r.status]}`}>{r.status}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{r.parent_name} · {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString('en-IN') : 'Not submitted'}</p>
                </div>
                <ScorePill value={r.overall_score} small />
                {expanded === r.id ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
              </div>
              {expanded === r.id && (
                <div className="border-t border-gray-100 p-4 bg-gray-50">
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {DIMENSIONS.map(d => (
                      <div key={d.key} className="text-center">
                        <p className="text-xs text-gray-400">{d.label.split(' ')[0]}</p>
                        <ScorePill value={r[d.key]} small />
                      </div>
                    ))}
                  </div>
                  {(r.improvement_areas?.length > 0 || r.concern_categories?.length > 0) && (
                    <div className="flex gap-2 flex-wrap mb-3">
                      {r.improvement_areas?.map(a => <span key={a} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{a}</span>)}
                      {r.concern_categories?.map(a => <span key={a} className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full">{a}</span>)}
                    </div>
                  )}
                  {r.appreciate_text && <p className="text-xs text-gray-600 mb-1"><span className="font-semibold text-emerald-600">Appreciate: </span>{r.appreciate_text}</p>}
                  {r.improve_text && <p className="text-xs text-gray-600 mb-1"><span className="font-semibold text-amber-600">Improve: </span>{r.improve_text}</p>}
                  {r.management_concern && <p className="text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2 mt-1"><span className="font-semibold">⚠ Management: </span>{r.management_concern}</p>}
                  <div className="flex justify-end mt-2">
                    <button className="text-xs text-red-500 hover:text-red-700" onClick={() => handleDelete(r.id)}>Delete</button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {responses.length === 0 && <p className="text-center text-gray-400 py-10">No responses found</p>}
        </div>
      )}
    </div>
  );
}

// ── Tab: Add Response (manual entry) ──────────────────────
function AddResponseTab({ campaignId, onRefresh }) {
  const blank = { student_name: '', admission_no: '', class_name: '', section: '', parent_name: '', parent_mobile: '', parent_email: '', class_teacher: '', coordinator: '', academic_progress: '', teacher_effectiveness: '', communication: '', student_development: '', school_services: '', recommendation: '', improvement_areas: [], concern_categories: [], appreciate_text: '', improve_text: '', management_concern: '', status: 'submitted' };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const toggleMulti = (field, val) => setForm(p => ({ ...p, [field]: p[field].includes(val) ? p[field].filter(x => x !== val) : [...p[field], val] }));

  const handleSave = async () => {
    if (!form.class_name || !form.academic_progress) return toast.error('Class and scores are required');
    setSaving(true);
    try {
      await ptcAPI.createResponse(campaignId, form);
      toast.success('Response saved');
      setForm(blank);
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    setSaving(false);
  };

  const numInput = (field) => (
    <input type="number" min="1" max="10" step="0.1" className={inp} placeholder="1–10" value={form[field]} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} />
  );

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-gray-500 mb-4">Manually enter a parent feedback response for this campaign.</p>
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Student Name"><input className={inp} value={form.student_name} onChange={e => setForm(p=>({...p,student_name:e.target.value}))} /></Field>
          <Field label="Admission No"><input className={inp} value={form.admission_no} onChange={e => setForm(p=>({...p,admission_no:e.target.value}))} /></Field>
          <Field label="Class *"><input className={inp} placeholder="e.g. Grade 5A" value={form.class_name} onChange={e => setForm(p=>({...p,class_name:e.target.value}))} /></Field>
          <Field label="Parent Name"><input className={inp} value={form.parent_name} onChange={e => setForm(p=>({...p,parent_name:e.target.value}))} /></Field>
        </div>
        <hr className="border-gray-100" />
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Section Scores (1–10)</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Academic Progress *">{numInput('academic_progress')}</Field>
          <Field label="Teacher Effectiveness *">{numInput('teacher_effectiveness')}</Field>
          <Field label="Communication *">{numInput('communication')}</Field>
          <Field label="Student Development *">{numInput('student_development')}</Field>
          <Field label="School Services">{numInput('school_services')}</Field>
          <Field label="Recommendation (NPS)">{numInput('recommendation')}</Field>
        </div>
        <hr className="border-gray-100" />
        <div>
          <SL>Child Improvement Areas</SL>
          <div className="flex flex-wrap gap-2 mt-1">
            {IMPROVEMENT_OPTIONS.map(o => (
              <button key={o} type="button" onClick={() => toggleMulti('improvement_areas', o)}
                className={`text-xs px-2 py-1 rounded-full border transition-colors ${form.improvement_areas.includes(o) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>{o}</button>
            ))}
          </div>
        </div>
        <div>
          <SL>Concern Categories</SL>
          <div className="flex flex-wrap gap-2 mt-1">
            {CONCERN_OPTIONS.map(o => (
              <button key={o} type="button" onClick={() => toggleMulti('concern_categories', o)}
                className={`text-xs px-2 py-1 rounded-full border transition-colors ${form.concern_categories.includes(o) ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-600 border-gray-200 hover:border-red-300'}`}>{o}</button>
            ))}
          </div>
        </div>
        <Field label="What Parents Appreciate"><textarea className={inp} rows={2} value={form.appreciate_text} onChange={e => setForm(p=>({...p,appreciate_text:e.target.value}))} /></Field>
        <Field label="Improvement Suggestions"><textarea className={inp} rows={2} value={form.improve_text} onChange={e => setForm(p=>({...p,improve_text:e.target.value}))} /></Field>
        <Field label="Concern for Management"><textarea className={inp} rows={2} value={form.management_concern} onChange={e => setForm(p=>({...p,management_concern:e.target.value}))} /></Field>
        <div className="flex justify-end">
          <button className={btn()} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Response'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Tab: Action Tracker ────────────────────────────────────
function ActionTrackerTab({ campaignId }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal,   setModal]   = useState(null);
  const [filter,  setFilter]  = useState({ status: '', priority: '' });
  const [expanded, setExpanded] = useState(null);
  const [comments, setComments] = useState({});

  const load = useCallback(() => {
    if (!campaignId) return;
    setLoading(true);
    ptcAPI.getTickets(campaignId, filter)
      .then(r => setTickets(r.data))
      .catch(() => toast.error('Failed to load tickets'))
      .finally(() => setLoading(false));
  }, [campaignId, filter]);

  useEffect(load, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this ticket?')) return;
    try { await ptcAPI.deleteTicket(campaignId, id); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const loadComments = async (id) => {
    const r = await ptcAPI.getComments(id);
    setComments(p => ({ ...p, [id]: r.data }));
  };

  const toggleExpand = (id) => {
    if (expanded === id) { setExpanded(null); } else { setExpanded(id); loadComments(id); }
  };

  const stats = {
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    critical: tickets.filter(t => t.priority === 'critical').length,
  };

  if (!campaignId) return <div className="py-16 text-center text-gray-400">Select a campaign</div>;

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Open', val: stats.open, color: 'text-red-600 bg-red-50' },
          { label: 'In Progress', val: stats.in_progress, color: 'text-amber-600 bg-amber-50' },
          { label: 'Resolved', val: stats.resolved, color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Critical', val: stats.critical, color: 'text-red-800 bg-red-100' },
        ].map(s => (
          <div key={s.label} className={`rounded-xl p-3 text-center ${s.color}`}>
            <p className="text-xl font-black">{s.val}</p>
            <p className="text-xs font-medium">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-3 mb-4 flex-wrap items-center">
        <select className={`${inp} w-36`} value={filter.status} onChange={e => setFilter(p => ({ ...p, status: e.target.value }))}>
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select className={`${inp} w-36`} value={filter.priority} onChange={e => setFilter(p => ({ ...p, priority: e.target.value }))}>
          <option value="">All Priorities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <button className={btn('outline')} onClick={load}><RefreshCw size={13} /></button>
        <button className={`${btn()} ml-auto`} onClick={() => setModal('create')}><Plus size={14} /> New Ticket</button>
      </div>

      {loading ? <p className="text-center text-gray-400 py-10">Loading…</p> : (
        <div className="space-y-2">
          {tickets.map(t => (
            <div key={t.id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="flex items-start gap-3 p-4 cursor-pointer hover:bg-gray-50" onClick={() => toggleExpand(t.id)}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TICKET_STATUS_COLORS[t.status]}`}>{t.status.replace('_',' ')}</span>
                    {t.category && <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{t.category}</span>}
                  </div>
                  <p className="font-semibold text-gray-900 mt-1 text-sm">{t.title}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                    {t.class_name   && <span>📍 {t.class_name}</span>}
                    {t.assigned_to  && <span>👤 {t.assigned_to}</span>}
                    {t.due_date     && <span><Clock size={10} className="inline mr-0.5" />{t.due_date.slice(0,10)}</span>}
                    {t.comment_count > 0 && <span>💬 {t.comment_count}</span>}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button className="p-1.5 text-gray-300 hover:text-blue-500 transition-colors" onClick={e => { e.stopPropagation(); setModal(t); }}><Edit2 size={13} /></button>
                  <button className="p-1.5 text-gray-300 hover:text-red-500 transition-colors" onClick={e => { e.stopPropagation(); handleDelete(t.id); }}><Trash2 size={13} /></button>
                  {expanded === t.id ? <ChevronDown size={14} className="text-gray-300 mt-1" /> : <ChevronRight size={14} className="text-gray-300 mt-1" />}
                </div>
              </div>
              {expanded === t.id && (
                <div className="border-t border-gray-100 p-4 bg-gray-50">
                  {t.description && <p className="text-sm text-gray-600 mb-3">{t.description}</p>}
                  <CommentSection ticketId={t.id} comments={comments[t.id] || []} onRefresh={() => loadComments(t.id)} />
                </div>
              )}
            </div>
          ))}
          {tickets.length === 0 && <p className="text-center text-gray-400 py-10">No tickets found</p>}
        </div>
      )}

      {modal && (
        <TicketModal
          ticket={modal === 'create' ? null : modal}
          campaignId={campaignId}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function CommentSection({ ticketId, comments, onRefresh }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try { await ptcAPI.addComment(ticketId, { comment: text }); setText(''); onRefresh(); }
    catch { toast.error('Failed'); }
    setSending(false);
  };

  return (
    <div>
      {comments.length > 0 && (
        <div className="space-y-2 mb-3">
          {comments.map(c => (
            <div key={c.id} className="bg-white rounded-lg border border-gray-100 px-3 py-2">
              <div className="flex items-center gap-2 text-xs text-gray-400 mb-0.5">
                <span className="font-semibold text-gray-700">{c.commented_by}</span>
                {c.status_change && <span className="bg-blue-100 text-blue-700 px-1.5 rounded text-xs">→ {c.status_change}</span>}
                <span>{new Date(c.created_at).toLocaleString('en-IN', { dateStyle:'short', timeStyle:'short' })}</span>
              </div>
              <p className="text-sm text-gray-700">{c.comment}</p>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input className={`${inp} flex-1`} placeholder="Add comment…" value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} />
        <button className={btn()} onClick={send} disabled={sending}>Post</button>
      </div>
    </div>
  );
}

// ── SLOT SETUP TAB ─────────────────────────────────────────
const DURATION_OPTIONS = [5, 10, 15, 20, 30, 45, 60];
const PTC_TYPES = [{ v:'physical', l:'Physical' }, { v:'online', l:'Online' }, { v:'hybrid', l:'Hybrid' }];

function SlotConfigModal({ campaignId, config, onClose, onSave }) {
  const [form, setForm] = useState({
    class_name: '', section: '', teacher_name: '', coordinator_name: '',
    ptc_date: '', start_time: '08:00', end_time: '13:00',
    meeting_duration: 10, buffer_minutes: 0, max_per_slot: 1, max_daily: '',
    venue: '', meeting_link: '', break_times: [{ start: '10:30', end: '10:45' }],
    feedback_campaign_id: '',
    ...config,
    break_times: config?.break_times?.length ? config.break_times : [{ start: '10:30', end: '10:45' }],
    feedback_campaign_id: config?.feedback_campaign_id || '',
  });
  const [saving,         setSaving]         = useState(false);
  const [setupData,      setSetupData]      = useState({ classes: [], employees: [] });
  const [loadingSetup,   setLoadingSetup]   = useState(true);
  const [pubCampaigns,   setPubCampaigns]   = useState([]);

  // Load classes + employees + published campaigns on mount
  useEffect(() => {
    Promise.all([
      ptcBookingAPI.getSetupData(campaignId),
      ptcAPI.getCampaigns(),
    ])
      .then(([sd, cp]) => {
        setSetupData(sd.data);
        setPubCampaigns((cp.data || []).filter(c => c.status === 'published'));
      })
      .catch(() => toast.error('Could not load school data'))
      .finally(() => setLoadingSetup(false));
  }, [campaignId]);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // When class changes, clear section unless already valid for new class
  const handleClassChange = grade => {
    const classDef = setupData.classes.find(c => c.grade === grade);
    const sections = classDef?.sections || [];
    setForm(p => ({
      ...p,
      class_name: grade,
      section: sections.includes(p.section) ? p.section : (sections[0] || ''),
    }));
  };

  const sections = setupData.classes.find(c => c.grade === form.class_name)?.sections || [];

  const addBreak    = () => setF('break_times', [...form.break_times, { start: '12:00', end: '13:00' }]);
  const removeBreak = i  => setF('break_times', form.break_times.filter((_, j) => j !== i));
  const setBreak    = (i, k, v) => setF('break_times', form.break_times.map((b, j) => j === i ? { ...b, [k]: v } : b));

  const handle = async () => {
    if (!form.class_name || !form.ptc_date) return toast.error('Class and PTC date are required');
    setSaving(true);
    try {
      const fn = config?.id
        ? ptcBookingAPI.updateSlotConfig(campaignId, config.id, form)
        : ptcBookingAPI.createSlotConfig(campaignId, form);
      await fn;
      toast.success(config?.id ? 'Config updated' : 'Config created');
      onSave();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    setSaving(false);
  };

  const selCls  = `${inp} bg-white`;
  const empOpts = setupData.employees.map(e => (
    <option key={e.id} value={e.name}>{e.name}{e.designation ? ` — ${e.designation}` : ''}</option>
  ));

  return (
    <Modal title={config?.id ? 'Edit Slot Config' : 'New Slot Config'} onClose={onClose} wide>
      {loadingSetup ? (
        <div className="py-8 text-center text-gray-400 text-sm">Loading school data…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4">

            {/* GRADE — dropdown from ERP */}
            <Field label="Grade *">
              <select className={selCls} value={form.class_name} onChange={e => handleClassChange(e.target.value)}>
                <option value="">— Select grade —</option>
                {setupData.classes.map(c => (
                  <option key={c.grade} value={c.grade}>{c.grade}</option>
                ))}
              </select>
            </Field>

            {/* SECTION — dropdown filtered by selected class */}
            <Field label="Section">
              {sections.length > 0 ? (
                <select className={selCls} value={form.section || ''} onChange={e => setF('section', e.target.value)}>
                  <option value="">— All sections —</option>
                  {sections.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <input className={inp} value={form.section || ''} onChange={e => setF('section', e.target.value)}
                  placeholder={form.class_name ? 'No sections found' : 'Select class first'} disabled={!form.class_name} />
              )}
            </Field>

            {/* TEACHER — dropdown from employees */}
            <Field label="Class Teacher">
              <select className={selCls} value={form.teacher_name || ''} onChange={e => setF('teacher_name', e.target.value)}>
                <option value="">— Select teacher —</option>
                {empOpts}
              </select>
            </Field>

            {/* COORDINATOR — dropdown from employees */}
            <Field label="Coordinator">
              <select className={selCls} value={form.coordinator_name || ''} onChange={e => setF('coordinator_name', e.target.value)}>
                <option value="">— Select coordinator —</option>
                {empOpts}
              </select>
            </Field>

            {/* PTC DATE */}
            <Field label="PTC Date *">
              <input type="date" className={inp} value={form.ptc_date} onChange={e => setF('ptc_date', e.target.value)} />
            </Field>

            {/* MEETING DURATION */}
            <Field label="Meeting Duration">
              <select className={selCls} value={form.meeting_duration} onChange={e => setF('meeting_duration', Number(e.target.value))}>
                {DURATION_OPTIONS.map(d => <option key={d} value={d}>{d} min</option>)}
              </select>
            </Field>

            {/* START / END TIMES */}
            <Field label="Start Time">
              <input type="time" className={inp} value={form.start_time} onChange={e => setF('start_time', e.target.value)} />
            </Field>
            <Field label="End Time">
              <input type="time" className={inp} value={form.end_time} onChange={e => setF('end_time', e.target.value)} />
            </Field>

            {/* BUFFER + CAPACITY */}
            <Field label="Buffer Between Slots (min)">
              <input type="number" min={0} max={30} className={inp} value={form.buffer_minutes}
                onChange={e => setF('buffer_minutes', Number(e.target.value))} />
            </Field>
            <Field label="Max Bookings Per Slot">
              <input type="number" min={1} max={20} className={inp} value={form.max_per_slot}
                onChange={e => setF('max_per_slot', Number(e.target.value))} />
            </Field>

            {/* VENUE + LINK */}
            <Field label="Venue / Room">
              <input className={inp} value={form.venue || ''} onChange={e => setF('venue', e.target.value)}
                placeholder="Room 101 / Library / Hall A" />
            </Field>
            <Field label="Online Meeting Link">
              <input className={inp} value={form.meeting_link || ''} onChange={e => setF('meeting_link', e.target.value)}
                placeholder="https://meet.google.com/…" />
            </Field>
          </div>

          {/* FEEDBACK FORM */}
          <div className="col-span-2 mt-1">
            <Field label="Feedback Form (Post-Meeting)">
              <select
                className={`${inp} bg-white`}
                value={form.feedback_campaign_id || ''}
                onChange={e => setF('feedback_campaign_id', e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">— Use current campaign's form (default) —</option>
                {pubCampaigns.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.questions
                      ? ` (${(() => { try { return JSON.parse(c.questions).length; } catch { return '?'; } })()} questions)`
                      : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                Select which published feedback form parents will see after the meeting. Leave blank to use this campaign's own questions.
              </p>
            </Field>
          </div>

          {/* BREAKS */}
          <div className="mt-3">
            <div className="flex items-center justify-between mb-2">
              <SL>Break / Lunch Times</SL>
              <button onClick={addBreak} className="text-xs text-primary-600 hover:underline flex items-center gap-1">
                <Plus size={12}/>Add Break
              </button>
            </div>
            {form.break_times.map((b, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <input type="time" className={`${inp} flex-1`} value={b.start} onChange={e => setBreak(i,'start',e.target.value)} />
                <span className="text-gray-400 text-xs">to</span>
                <input type="time" className={`${inp} flex-1`} value={b.end}   onChange={e => setBreak(i,'end',e.target.value)} />
                <button onClick={() => removeBreak(i)} className="text-red-400 hover:text-red-600"><X size={14}/></button>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="flex gap-3 pt-4 border-t mt-4">
        <button onClick={onClose} className="flex-1 border border-gray-200 rounded-xl py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
          Cancel
        </button>
        <button onClick={handle} disabled={saving || loadingSetup}
          className="flex-1 bg-primary-600 text-white rounded-xl py-2 text-sm font-semibold disabled:opacity-50">
          {saving ? 'Saving…' : config?.id ? 'Update Config' : 'Create Config'}
        </button>
      </div>
    </Modal>
  );
}

function SlotSetupTab({ campaignId, campaign }) {
  const [configs, setConfigs]           = useState([]);
  const [editConfig, setEditConfig]     = useState(null);
  const [showModal, setShowModal]       = useState(false);
  const [loading, setLoading]           = useState(false);
  const [generating, setGenerating]     = useState(false);
  const [genTokens, setGenTokens]       = useState(false);
  const [slotPreview, setSlotPreview]   = useState(null);
  const [showLinks, setShowLinks]       = useState(false);
  const [bookingTokens, setBookingTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [linkFilter, setLinkFilter]     = useState('');
  const [copiedTok, setCopiedTok]       = useState(null);

  const bookingBase = `${window.location.origin}/ptc-book/`;

  const loadBookingTokens = useCallback(async () => {
    if (!campaignId) return;
    setTokensLoading(true);
    try {
      const r = await ptcBookingAPI.getBookingTokens(campaignId);
      setBookingTokens(r.data || []);
    } catch { toast.error('Failed to load booking links'); }
    setTokensLoading(false);
  }, [campaignId]);

  const handleViewLinks = () => {
    setShowLinks(true);
    loadBookingTokens();
  };

  const copyBookingLink = (tok) => {
    navigator.clipboard.writeText(`${bookingBase}${tok.token}`).then(() => {
      setCopiedTok(tok.id);
      toast.success('Booking link copied');
      setTimeout(() => setCopiedTok(null), 2000);
    });
  };

  const copyAllBookingLinks = () => {
    const filtered = bookingTokens.filter(t => {
      if (!linkFilter) return true;
      const q = linkFilter.toLowerCase();
      return (t.student_name||'').toLowerCase().includes(q) || (t.class_name||'').toLowerCase().includes(q);
    });
    const lines = filtered.map(t => `${t.student_name} (${t.class_name}): ${bookingBase}${t.token}`).join('\n');
    navigator.clipboard.writeText(lines).then(() => toast.success(`${filtered.length} links copied`));
  };

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try { const r = await ptcBookingAPI.getSlotConfigs(campaignId); setConfigs(r.data); }
    catch { toast.error('Failed to load slot configs'); }
    setLoading(false);
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async id => {
    try { await ptcBookingAPI.deleteSlotConfig(campaignId, id); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const handleGenerate = async (configId) => {
    setGenerating(true);
    try {
      const r = await ptcBookingAPI.generateSlots(campaignId, configId ? { config_ids: [configId] } : {});
      toast.success(`Generated ${r.data.generated} slots across ${r.data.configs_processed} classes`);
    } catch (e) { toast.error(e.response?.data?.error || 'Generation failed'); }
    setGenerating(false);
  };

  const handleGenTokens = async () => {
    setGenTokens(true);
    try {
      const r = await ptcBookingAPI.generateBookingTokens(campaignId);
      toast.success(`Generated ${r.data.generated} booking tokens (${r.data.skipped} already existed)`);
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    setGenTokens(false);
  };

  const handlePreview = async id => {
    const cfg = configs.find(c => c.id === id);
    if (!cfg) return;
    // Client-side preview calculation
    const dur = parseInt(cfg.meeting_duration) || 10;
    const buf = parseInt(cfg.buffer_minutes) || 0;
    const timeToMin = t => { const [h,m] = t.split(':').map(Number); return h*60+m; };
    const minToTime = m => `${Math.floor(m/60).toString().padStart(2,'0')}:${(m%60).toString().padStart(2,'0')}`;
    const toMin = t => timeToMin(t.slice(0,5));
    const breaks = (cfg.break_times || []).map(b => ({ s: toMin(b.start||b.s), e: toMin(b.end||b.e) }));
    let cur = toMin(cfg.start_time.slice(0,5));
    const end = toMin(cfg.end_time.slice(0,5));
    const slots = [];
    while (cur + dur <= end) {
      const se = cur + dur;
      const brk = breaks.find(b => cur < b.e && se > b.s);
      if (brk) { cur = brk.e; continue; }
      slots.push(`${minToTime(cur)} – ${minToTime(se)}`);
      cur = se + buf;
    }
    setSlotPreview({ class_name: cfg.class_name, slots });
  };

  if (!campaignId) return <div className="py-12 text-center text-gray-400">Select a campaign first</div>;

  const feedbackQuestions = (() => {
    try { return typeof campaign?.questions === 'string' ? JSON.parse(campaign.questions) : (campaign?.questions || []); }
    catch { return []; }
  })();
  const formLayout = (() => {
    try { return typeof campaign?.form_layout === 'string' ? JSON.parse(campaign.form_layout) : (campaign?.form_layout || {}); }
    catch { return {}; }
  })();

  return (
    <div className="space-y-6">
      {/* Feedback form info banner */}
      {campaign && (
        <div className="bg-white border border-gray-100 rounded-2xl px-4 py-3 flex items-center justify-between gap-4 shadow-sm">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <ClipboardList size={16} className="text-emerald-600"/>
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Post-Meeting Feedback Form</p>
              <p className="text-sm font-semibold text-gray-800 truncate">
                {feedbackQuestions.length} question{feedbackQuestions.length !== 1 ? 's' : ''} configured
                {formLayout.template && <span className="ml-2 text-xs font-normal text-gray-400 capitalize">· {formLayout.template} theme</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 text-xs">
            {campaign.has_slot_booking
              ? <span className="bg-emerald-100 text-emerald-700 font-semibold px-2.5 py-1 rounded-full">Slot Booking ON</span>
              : <span className="bg-amber-100 text-amber-700 font-semibold px-2.5 py-1 rounded-full">Slot Booking OFF</span>
            }
            <span className="text-gray-300">|</span>
            <span className="text-gray-500 capitalize">
              {campaign.ptc_type === 'online' ? '💻 Online' : campaign.ptc_type === 'hybrid' ? '🔀 Hybrid' : '🏫 Physical'}
            </span>
          </div>
        </div>
      )}

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => { setEditConfig(null); setShowModal(true); }}
          className={btn()}>
          <Plus size={14}/>Add Class Config
        </button>
        <button onClick={() => handleGenerate(null)} disabled={generating || configs.length===0}
          className={btn('outline')}>
          <Zap size={14}/>{generating ? 'Generating…' : 'Generate All Slots'}
        </button>
        <button onClick={handleGenTokens} disabled={genTokens}
          className={btn('outline')}>
          <Key size={14}/>{genTokens ? 'Generating…' : 'Generate Booking Links'}
        </button>
        <button onClick={handleViewLinks} className={btn('outline')}>
          <Link2 size={14}/> View / Share Links
        </button>
      </div>

      {loading ? <div className="py-12 text-center text-gray-400">Loading…</div> : configs.length === 0 ? (
        <div className="py-16 text-center">
          <Calendar size={40} className="text-gray-200 mx-auto mb-3"/>
          <p className="font-semibold text-gray-500">No slot configs yet</p>
          <p className="text-sm text-gray-400 mt-1">Add a class configuration to define meeting timings</p>
        </div>
      ) : (
        <div className="space-y-3">
          {configs.map(cfg => (
            <div key={cfg.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-gray-900">
                      {cfg.class_name}{cfg.section ? ` – ${cfg.section}` : ''}
                    </span>
                    <span className="text-xs bg-primary-50 text-primary-700 font-semibold px-2 py-0.5 rounded-full">
                      {cfg.meeting_duration} min slots
                    </span>
                    {cfg.buffer_minutes > 0 && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">+{cfg.buffer_minutes}m buffer</span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                    <span className="flex items-center gap-1"><Calendar size={12}/>{cfg.ptc_date}</span>
                    <span><Clock size={12} className="inline mr-1"/>{cfg.start_time?.slice(0,5)} – {cfg.end_time?.slice(0,5)}</span>
                    {cfg.teacher_name && <span>👩‍🏫 {cfg.teacher_name}</span>}
                    {cfg.venue && <span className="flex items-center gap-1"><MapPin size={12}/>{cfg.venue}</span>}
                  </div>
                  {cfg.break_times?.length > 0 && (
                    <div className="mt-1 text-xs text-gray-400">
                      Breaks: {cfg.break_times.map(b => `${(b.start||b.s||'').slice(0,5)}–${(b.end||b.e||'').slice(0,5)}`).join(', ')}
                    </div>
                  )}
                  {cfg.feedback_campaign_id && (
                    <div className="mt-1 text-xs text-emerald-600 flex items-center gap-1">
                      <ClipboardList size={10}/>
                      Feedback: {cfg.feedback_campaign_name || `Campaign #${cfg.feedback_campaign_id}`}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => handlePreview(cfg.id)} title="Preview slots"
                    className="p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-primary-50 transition-colors">
                    <Eye size={14}/>
                  </button>
                  <button onClick={() => handleGenerate(cfg.id)} disabled={generating} title="Generate slots"
                    className="p-1.5 text-gray-400 hover:text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors">
                    <Zap size={14}/>
                  </button>
                  <button onClick={() => { setEditConfig(cfg); setShowModal(true); }}
                    className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors">
                    <Edit2 size={14}/>
                  </button>
                  <button onClick={() => handleDelete(cfg.id)}
                    className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Slot preview modal */}
      {slotPreview && (
        <Modal title={`Slot Preview — ${slotPreview.class_name}`} onClose={() => setSlotPreview(null)}>
          <p className="text-sm text-gray-500 mb-3">{slotPreview.slots.length} slots will be generated</p>
          <div className="flex flex-wrap gap-2 max-h-60 overflow-y-auto">
            {slotPreview.slots.map((s, i) => (
              <span key={i} className="text-xs bg-primary-50 text-primary-700 px-2.5 py-1 rounded-lg font-mono">{s}</span>
            ))}
          </div>
        </Modal>
      )}

      {showModal && (
        <SlotConfigModal
          campaignId={campaignId}
          config={editConfig}
          onClose={() => { setShowModal(false); setEditConfig(null); }}
          onSave={() => { setShowModal(false); setEditConfig(null); load(); }}
        />
      )}

      {/* Booking Links Modal */}
      {showLinks && (
        <Modal title="Booking Links — Share with Parents" onClose={() => { setShowLinks(false); setLinkFilter(''); }} wide>
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            {[
              { label: 'Total Links', val: bookingTokens.length, color: 'bg-blue-50 text-blue-700' },
              { label: 'Used', val: bookingTokens.filter(t => t.is_used).length, color: 'bg-emerald-50 text-emerald-700' },
              { label: 'Pending', val: bookingTokens.filter(t => !t.is_used).length, color: 'bg-amber-50 text-amber-700' },
            ].map(s => (
              <div key={s.label} className={`rounded-xl p-3 text-center ${s.color}`}>
                <p className="text-xl font-black">{s.val}</p>
                <p className="text-xs font-medium mt-0.5 opacity-75">{s.label}</p>
              </div>
            ))}
          </div>

          {/* How-to banner */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-700 mb-4">
            <p className="font-bold mb-1">📋 How to share:</p>
            <ol className="list-decimal pl-4 space-y-0.5">
              <li>Copy individual links below and send via WhatsApp/SMS to each parent.</li>
              <li>Or use <strong>Copy All</strong> to bulk-copy and paste into a spreadsheet/message blast.</li>
              <li>Parents open the link → pick a slot for each child → booking confirmed instantly.</li>
            </ol>
          </div>

          {/* Filter + actions */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <input
              className="flex-1 min-w-[160px] border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              placeholder="Filter by student or grade…"
              value={linkFilter}
              onChange={e => setLinkFilter(e.target.value)}
            />
            {bookingTokens.length > 0 && (
              <button className={btn('outline')} onClick={copyAllBookingLinks}>
                <Copy size={13}/> Copy All
              </button>
            )}
            <button className={btn('outline')} onClick={loadBookingTokens} disabled={tokensLoading}>
              <RefreshCw size={13} className={tokensLoading ? 'animate-spin' : ''}/> Refresh
            </button>
          </div>

          {tokensLoading ? (
            <div className="flex items-center justify-center py-12 text-gray-400 gap-2">
              <Loader2 size={18} className="animate-spin"/> Loading…
            </div>
          ) : bookingTokens.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <Key size={32} className="mx-auto mb-2 opacity-40"/>
              <p className="font-medium text-sm">No booking links yet</p>
              <p className="text-xs mt-1">Click "Generate Booking Links" first to create parent URLs</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
              {bookingTokens
                .filter(t => {
                  if (!linkFilter) return true;
                  const q = linkFilter.toLowerCase();
                  return (t.student_name||'').toLowerCase().includes(q) || (t.class_name||'').toLowerCase().includes(q);
                })
                .map(tok => (
                  <div key={tok.id} className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${tok.is_used ? 'border-emerald-100 bg-emerald-50/40' : 'border-gray-100 bg-white'}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-gray-900 truncate">{tok.student_name || '—'}</span>
                        <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{tok.class_name}</span>
                        {tok.section && <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{tok.section}</span>}
                        {tok.is_used && (
                          <span className="text-xs text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1">
                            <CheckCheck size={10}/> Booked
                          </span>
                        )}
                      </div>
                      {tok.parent_name && <p className="text-xs text-gray-400 mt-0.5">Parent: {tok.parent_name}{tok.parent_mobile ? ` · ${tok.parent_mobile}` : ''}</p>}
                      <p className="text-xs text-gray-300 font-mono mt-0.5 truncate">{bookingBase}{tok.token}</p>
                    </div>
                    <button
                      onClick={() => copyBookingLink(tok)}
                      title="Copy booking link"
                      className="p-1.5 rounded-lg hover:bg-primary-50 text-gray-400 hover:text-primary-600 transition-colors flex-shrink-0"
                    >
                      {copiedTok === tok.id ? <CheckCheck size={15} className="text-emerald-500"/> : <Copy size={15}/>}
                    </button>
                  </div>
                ))
              }
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ── BOOKINGS TAB ───────────────────────────────────────────
const BOOKING_STATUS_COLORS = {
  booked:      'bg-blue-100 text-blue-700',
  attended:    'bg-emerald-100 text-emerald-700',
  no_show:     'bg-red-100 text-red-700',
  cancelled:   'bg-gray-100 text-gray-500',
  rescheduled: 'bg-amber-100 text-amber-700',
};

function BookingsTab({ campaignId }) {
  const [bookings, setBookings]       = useState([]);
  const [stats, setStats]             = useState(null);
  const [expanded, setExpanded]       = useState(null);
  const [loading, setLoading]         = useState(false);
  const [filters, setFilters]         = useState({ class_name: '', status: '', date: '' });
  const [notes, setNotes]             = useState({});
  const [saving, setSaving]           = useState(null);

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const [br, sr] = await Promise.all([
        ptcBookingAPI.getBookings(campaignId, filters),
        ptcBookingAPI.getBookingStats(campaignId),
      ]);
      setBookings(br.data);
      setStats(sr.data);
    } catch { toast.error('Failed to load bookings'); }
    setLoading(false);
  }, [campaignId, filters]);

  useEffect(() => { load(); }, [load]);

  const handleStatus = async (bid, status) => {
    setSaving(bid);
    try {
      await ptcBookingAPI.updateBooking(campaignId, bid, {
        status,
        meeting_notes: notes[bid] || undefined,
      });
      toast.success(status === 'attended' ? 'Marked as Attended — feedback link generated!' : `Marked as ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Update failed'); }
    setSaving(null);
  };

  const handleSaveNotes = async bid => {
    setSaving(bid);
    try {
      await ptcBookingAPI.updateBooking(campaignId, bid, { meeting_notes: notes[bid] || '' });
      toast.success('Notes saved');
    } catch { toast.error('Save failed'); }
    setSaving(null);
  };

  if (!campaignId) return <div className="py-12 text-center text-gray-400">Select a campaign first</div>;

  const T = stats?.totals || {};
  const kpis = [
    { label:'Total Booked', value: T.total_bookings||0, color:'text-primary-600', bg:'bg-primary-50' },
    { label:'Attended',     value: T.attended||0,       color:'text-emerald-600',  bg:'bg-emerald-50' },
    { label:'No Show',      value: T.no_shows||0,       color:'text-red-600',      bg:'bg-red-50' },
    { label:'Upcoming',     value: T.upcoming||0,       color:'text-blue-600',     bg:'bg-blue-50' },
    { label:'Feedback Done',value: T.feedback_submitted||0, color:'text-purple-600', bg:'bg-purple-50' },
  ];

  return (
    <div className="space-y-5">
      {/* KPI row */}
      <div className="grid grid-cols-5 gap-3">
        {kpis.map(k => (
          <div key={k.label} className={`${k.bg} rounded-2xl p-4 text-center`}>
            <p className={`text-2xl font-black ${k.color}`}>{k.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{k.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center bg-white border border-gray-100 rounded-2xl p-3">
        <Filter size={14} className="text-gray-400"/>
        <input type="date" className={`${inp} py-1 text-xs`} value={filters.date}
          onChange={e => setFilters(p => ({...p, date: e.target.value}))} />
        <input className={`${inp} py-1 text-xs flex-1 min-w-[120px]`} placeholder="Filter by class…"
          value={filters.class_name} onChange={e => setFilters(p => ({...p, class_name: e.target.value}))} />
        <select className={`${inp} py-1 text-xs`} value={filters.status}
          onChange={e => setFilters(p => ({...p, status: e.target.value}))}>
          <option value="">All statuses</option>
          {['booked','attended','no_show','cancelled','rescheduled'].map(s =>
            <option key={s} value={s}>{s.replace('_',' ')}</option>
          )}
        </select>
        <button onClick={load} className={`${btn('outline')} py-1 text-xs`}>
          <RefreshCw size={12}/>Refresh
        </button>
      </div>

      {loading ? <div className="py-12 text-center text-gray-400">Loading…</div> :
       bookings.length === 0 ? (
        <div className="py-16 text-center">
          <UserCheck size={40} className="text-gray-200 mx-auto mb-3"/>
          <p className="font-semibold text-gray-500">No bookings yet</p>
        </div>
       ) : (
        <div className="space-y-2">
          {bookings.map(bk => (
            <div key={bk.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div
                className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpanded(expanded === bk.id ? null : bk.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">{bk.student_name}</span>
                    <span className="text-xs text-gray-400">{bk.class_name}{bk.section ? `-${bk.section}` : ''}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${BOOKING_STATUS_COLORS[bk.booking_status || bk.status] || 'bg-gray-100 text-gray-600'}`}>
                      {(bk.booking_status||bk.status)?.replace('_',' ')}
                    </span>
                    {bk.follow_up_required ? <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Follow-up</span> : null}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5 flex gap-3 flex-wrap">
                    <span>{bk.slot_date} · {bk.slot_time?.slice(0,5)}{bk.slot_end_time ? `–${bk.slot_end_time.slice(0,5)}` : ''}</span>
                    {bk.parent_name && <span>Parent: {bk.parent_name}</span>}
                    {bk.venue && <span><MapPin size={10} className="inline"/>  {bk.venue}</span>}
                    <span className="font-mono text-gray-300">{bk.booking_ref}</span>
                  </div>
                </div>
                <ChevronDown size={16} className={`text-gray-400 transition-transform ${expanded===bk.id?'rotate-180':''}`}/>
              </div>

              {expanded === bk.id && (
                <div className="border-t border-gray-50 p-4 bg-gray-50 space-y-4">
                  {bk.special_notes && (
                    <div className="text-sm text-gray-600 bg-blue-50 rounded-xl px-3 py-2">
                      <span className="font-semibold">Parent's note:</span> {bk.special_notes}
                    </div>
                  )}

                  {/* Status buttons */}
                  {['booked','rescheduled'].includes(bk.status) && (
                    <div className="flex gap-2">
                      <button onClick={() => handleStatus(bk.id, 'attended')} disabled={saving===bk.id}
                        className="flex items-center gap-1.5 bg-emerald-600 text-white text-xs font-semibold px-3 py-2 rounded-xl hover:bg-emerald-700 disabled:opacity-50">
                        <CheckCircle size={13}/>Mark Attended
                      </button>
                      <button onClick={() => handleStatus(bk.id, 'no_show')} disabled={saving===bk.id}
                        className="flex items-center gap-1.5 bg-red-100 text-red-700 text-xs font-semibold px-3 py-2 rounded-xl hover:bg-red-200 disabled:opacity-50">
                        <XCircle size={13}/>No Show
                      </button>
                    </div>
                  )}
                  {bk.status === 'attended' && bk.feedback_token && (
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2.5">
                      <p className="text-xs font-bold text-emerald-700 mb-1.5">✅ Feedback link ready — share with parent</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs text-emerald-700 font-mono bg-emerald-100 rounded-lg px-2 py-1.5 truncate">
                          {window.location.origin}/feedback/{bk.feedback_token}
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/feedback/${bk.feedback_token}`);
                            toast.success('Feedback link copied');
                          }}
                          title="Copy feedback link"
                          className="flex-shrink-0 p-1.5 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-700 transition-colors"
                        >
                          <Copy size={13}/>
                        </button>
                      </div>
                      <p className="text-xs text-emerald-600 opacity-70 mt-1">Send this link to the parent to collect post-meeting feedback.</p>
                    </div>
                  )}
                  {bk.status === 'attended' && !bk.feedback_token && (
                    <p className="text-xs text-gray-400 bg-gray-50 px-3 py-2 rounded-xl">
                      ⏳ Feedback link generating…
                    </p>
                  )}

                  {/* Meeting notes */}
                  <div>
                    <SL>Meeting Notes</SL>
                    <textarea rows={3} className="inp w-full text-sm resize-none"
                      placeholder="Add meeting notes, concerns discussed…"
                      value={notes[bk.id] ?? (bk.meeting_notes || '')}
                      onChange={e => setNotes(p => ({...p, [bk.id]: e.target.value}))}
                    />
                    <button onClick={() => handleSaveNotes(bk.id)} disabled={saving===bk.id}
                      className="mt-1.5 text-xs bg-primary-600 text-white px-3 py-1.5 rounded-lg hover:bg-primary-700 disabled:opacity-50">
                      {saving===bk.id ? 'Saving…' : 'Save Notes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── MEETINGS TAB (Teacher View) ────────────────────────────
function MeetingsTab() {
  const [meetings, setMeetings]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [date, setDate]           = useState(new Date().toISOString().slice(0, 10));
  const [expanded, setExpanded]   = useState(null);
  const [notes, setNotes]         = useState({});
  const [saving, setSaving]       = useState(null);
  const [campaignId, setCampaignId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ptcBookingAPI.getMyMeetings({ date, campaign_id: campaignId || undefined });
      setMeetings(r.data);
    } catch { toast.error('Failed to load meetings'); }
    setLoading(false);
  }, [date, campaignId]);

  useEffect(() => { load(); }, [load]);

  const handleStatus = async (cid, bid, status) => {
    setSaving(bid);
    try {
      await ptcBookingAPI.updateBooking(cid, bid, {
        status,
        meeting_notes: notes[bid] || undefined,
      });
      toast.success(status === 'attended' ? '✅ Marked Attended — feedback link sent!' : `Marked ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    setSaving(null);
  };

  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = meetings.filter(m => m.slot_date === date);
  const attended = meetings.filter(m => m.status === 'attended').length;
  const upcoming = meetings.filter(m => m.status === 'booked').length;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3 bg-white border border-gray-100 rounded-2xl p-3">
        <input type="date" className={`${inp} py-1 text-xs`} value={date} onChange={e => setDate(e.target.value)} />
        <button onClick={() => setDate(today)} className="text-xs text-primary-600 font-semibold hover:underline">Today</button>
        <button onClick={load} className={`${btn('outline')} py-1 text-xs ml-auto`}>
          <RefreshCw size={12}/>Refresh
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          { l:'My Meetings', v:meetings.length, c:'text-primary-600', bg:'bg-primary-50' },
          { l:'Attended',    v:attended,          c:'text-emerald-600', bg:'bg-emerald-50' },
          { l:'Upcoming',    v:upcoming,          c:'text-blue-600',   bg:'bg-blue-50' },
        ].map(k => (
          <div key={k.l} className={`${k.bg} rounded-2xl p-4 text-center`}>
            <p className={`text-2xl font-black ${k.c}`}>{k.v}</p>
            <p className="text-xs text-gray-500 mt-0.5">{k.l}</p>
          </div>
        ))}
      </div>

      {loading ? <div className="py-12 text-center text-gray-400">Loading…</div> :
       meetings.length === 0 ? (
        <div className="py-16 text-center">
          <Calendar size={40} className="text-gray-200 mx-auto mb-3"/>
          <p className="font-semibold text-gray-500">No meetings on {date}</p>
          <p className="text-sm text-gray-400 mt-1">Select a different date or check if slot booking is open</p>
        </div>
       ) : (
        <div className="space-y-2">
          {meetings.map(m => (
            <div key={m.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpanded(expanded === m.id ? null : m.id)}>
                <div className="w-16 text-center flex-shrink-0">
                  <p className="text-sm font-black text-primary-700">{m.slot_time?.slice(0,5)}</p>
                  <p className="text-xs text-gray-400">{m.slot_end_time?.slice(0,5)}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">{m.student_name}</span>
                    <span className="text-xs text-gray-400">{m.class_name}{m.section ? `-${m.section}` : ''}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${BOOKING_STATUS_COLORS[m.status]}`}>
                      {m.status?.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    Parent: {m.parent_name} · {m.parent_mobile}
                    {m.venue ? ` · 📍 ${m.venue}` : ''}
                  </div>
                </div>
                <ChevronDown size={16} className={`text-gray-400 transition-transform ${expanded===m.id?'rotate-180':''}`}/>
              </div>

              {expanded === m.id && (
                <div className="border-t border-gray-50 p-4 bg-gray-50 space-y-3">
                  {m.special_notes && (
                    <p className="text-sm text-gray-600 bg-blue-50 rounded-xl px-3 py-2">
                      <span className="font-semibold">Parent note:</span> {m.special_notes}
                    </p>
                  )}
                  {m.meeting_link && (
                    <a href={m.meeting_link} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2 text-xs text-primary-600 bg-primary-50 px-3 py-2 rounded-xl hover:underline">
                      <Globe size={12}/>Join Meeting
                    </a>
                  )}

                  {m.status === 'booked' && (
                    <div className="flex gap-2">
                      <button onClick={() => handleStatus(m.campaign_id, m.id, 'attended')} disabled={saving===m.id}
                        className="flex items-center gap-1.5 bg-emerald-600 text-white text-xs font-semibold px-3 py-2 rounded-xl hover:bg-emerald-700 disabled:opacity-50">
                        <CheckCircle size={13}/>Mark Attended
                      </button>
                      <button onClick={() => handleStatus(m.campaign_id, m.id, 'no_show')} disabled={saving===m.id}
                        className="flex items-center gap-1.5 bg-red-100 text-red-700 text-xs font-semibold px-3 py-2 rounded-xl hover:bg-red-200 disabled:opacity-50">
                        <XCircle size={13}/>No Show
                      </button>
                    </div>
                  )}

                  <div>
                    <SL>Meeting Notes</SL>
                    <textarea rows={3} className="inp w-full text-sm resize-none"
                      placeholder="Discussion points, concerns, follow-up actions…"
                      value={notes[m.id] ?? (m.meeting_notes || '')}
                      onChange={e => setNotes(p => ({...p, [m.id]: e.target.value}))}
                    />
                    <button onClick={async () => {
                      setSaving(m.id);
                      try { await ptcBookingAPI.updateBooking(m.campaign_id, m.id, { meeting_notes: notes[m.id] || '' }); toast.success('Notes saved'); }
                      catch { toast.error('Failed'); }
                      setSaving(null);
                    }} disabled={saving===m.id}
                      className="mt-1.5 text-xs bg-primary-600 text-white px-3 py-1.5 rounded-lg disabled:opacity-50">
                      {saving===m.id ? 'Saving…' : 'Save Notes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────
const MAIN_TABS = [
  { id: 'dashboard', label: 'Dashboard',          icon: BarChart2  },
  { id: 'bookings',  label: 'Booking & Responses', icon: UserCheck  },
  { id: 'setup',     label: 'Setup',               icon: Settings   },
];

const BOOKING_SUBTABS = [
  { id: 'bookings',  label: 'Bookings',       icon: UserCheck    },
  { id: 'meetings',  label: 'My Meetings',    icon: BookOpen     },
  { id: 'responses', label: 'Responses',      icon: ClipboardList},
  { id: 'actions',   label: 'Action Tracker', icon: Ticket       },
];

const SETUP_SUBTABS = [
  { id: 'campaigns',  label: 'Campaigns',  icon: Megaphone },
  { id: 'slot-setup', label: 'Slot Setup', icon: Calendar  },
];

function SubTabs({ tabs, active, onChange }) {
  return (
    <div className="flex items-center gap-0.5 border-b border-gray-100 mb-5">
      {tabs.map(({ id, label, icon: Icon }) => (
        <button key={id} onClick={() => onChange(id)}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
            active === id
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
          }`}>
          <Icon size={13} />{label}
        </button>
      ))}
    </div>
  );
}

export default function ParentTeacherConnect() {
  const { yearId }  = useContext(YearContext);
  const [tab,        setTab]        = useState('dashboard');
  const [bookingTab, setBookingTab] = useState('bookings');
  const [setupTab,   setSetupTab]   = useState('campaigns');
  const [campaigns,  setCampaigns]  = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [selCampaign,setSelCampaign]= useState(null);

  const loadCampaigns = useCallback(() => {
    setLoading(true);
    ptcAPI.getCampaigns({ academic_year_id: yearId })
      .then(r => {
        setCampaigns(r.data);
        if (!selCampaign && r.data.length > 0) setSelCampaign(r.data[0].id);
      })
      .catch(() => toast.error('Failed to load campaigns'))
      .finally(() => setLoading(false));
  }, [yearId]);

  useEffect(loadCampaigns, [loadCampaigns]);

  const selectedCampaign = campaigns.find(c => c.id === selCampaign);

  return (
    <div className="p-6 space-y-5 max-w-screen-2xl mx-auto">

      {/* ── Header (Morning Activity style) ──────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold text-slate-800 flex items-center gap-2">
            <MessageSquare size={20} className="text-primary-600" />
            Parent Teacher Connect
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Collect feedback · Analyse performance · Drive action</p>
        </div>

        <div className="flex items-center gap-3 flex-wrap shrink-0">
          {/* Campaign selector */}
          {campaigns.length > 0 && (
            <div className="flex items-center gap-2">
              <select
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-400 bg-white min-w-[190px]"
                value={selCampaign || ''}
                onChange={e => setSelCampaign(Number(e.target.value))}
              >
                {campaigns.map(c => (
                  <option key={c.id} value={c.id}>{c.name} ({c.status})</option>
                ))}
              </select>
              {selectedCampaign && (
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[selectedCampaign.status]}`}>
                  {selectedCampaign.submitted_count || 0} responses
                </span>
              )}
            </div>
          )}

          {/* Main tab pill switcher */}
          <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
            {MAIN_TABS.map(({ id, label, icon: Icon }) => (
              <button key={id} onClick={() => setTab(id)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors ${
                  tab === id ? 'bg-primary-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}>
                <Icon size={13} />{label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content ───────────────────────────────────────────── */}
      {loading && campaigns.length === 0 ? (
        <div className="py-20 text-center text-gray-400">Loading…</div>
      ) : (
        <>
          {/* DASHBOARD */}
          {tab === 'dashboard' && (
            <DashboardTab campaignId={selCampaign} />
          )}

          {/* BOOKING & RESPONSES */}
          {tab === 'bookings' && (
            <div>
              <SubTabs tabs={BOOKING_SUBTABS} active={bookingTab} onChange={setBookingTab} />
              {bookingTab === 'bookings'  && <BookingsTab  campaignId={selCampaign} />}
              {bookingTab === 'meetings'  && <MeetingsTab />}
              {bookingTab === 'responses' && <ResponsesTab campaignId={selCampaign} />}
              {bookingTab === 'actions'   && <ActionTrackerTab campaignId={selCampaign} />}
            </div>
          )}

          {/* SETUP */}
          {tab === 'setup' && (
            <div>
              <SubTabs tabs={SETUP_SUBTABS} active={setupTab} onChange={setSetupTab} />
              {setupTab === 'campaigns'  && <CampaignsTab campaigns={campaigns} yearId={yearId} onRefresh={loadCampaigns} />}
              {setupTab === 'slot-setup' && <SlotSetupTab campaignId={selCampaign} campaign={selectedCampaign} />}
            </div>
          )}
        </>
      )}
    </div>
  );
}
